// 得意先向け請求書の純ロジック（2026-08-28 オーナー決定・様式は既存PDFに合わせる）。DOM非依存。
//
// 様式の正本は `エースジャパン/請求データ/【請求書】ガットリベロ㈱様_202607.pdf`。
// 採寸・端数処理・ページ分割の規則は同フォルダの請求書 **15件すべて**から機械的に復元した。
// 設計と根拠: engineering-タクミ/docs/2026-08-28-請求書発行-設計.md
//
// **通行料は内税**（この様式の最重要点）:
//   税抜通行料 = round(通行料合計 / 1.1)   ← half-up。切り捨て・銀行丸めは実物と合わない
//   内税額     = 通行料合計 − 税抜通行料
//   課税対象額 = 基本運賃合計 + 税抜通行料
//   消費税額   = round(課税対象額 * 0.10)  ← half-up
//   合計請求額 = 課税対象額 + 消費税額
// **サマリ帯の「通行料」は税抜・末尾内訳の「通行料」は税込。値が違う。**
//
// 注意: 既存 buildSheets の `total`（sub + tax + toll＝通行料を非課税の立替として扱う締め集計）は
// 請求書の合計請求額とは別の数。請求書は buildSheets の sub / toll だけを入力にして計算し直す。

// ---- 採寸（すべて px・96dpi。CSS px と 1:1。実物PDFの内容ストリームから実測） ----
export const LAYOUT = {
  pageW: 1122.56, pageH: 793.76,      // A4横
  tableLeft: 48.3, tableRight: 1077.8,
  // 月日/発地名/着地名/車番/品名/数量/単位/ｔ数/単価/基本運賃/割増・通行料/備考
  cols: [50.1, 140.0, 139.8, 60.0, 119.9, 69.9, 50.1, 39.8, 69.9, 100.0, 90.0, 100.0],
  headH: 38.5, rowH: 30.21,   // 行高は実測 (745.0-171.0)/19
  firstTop: 307.4, restTop: 132.5,
  frameBottomFirst: 738.6, frameBottomRest: 745.0,
}
export const ROWS_FIRST = 13  // 1ページ目の収容行数（上部ブロックがある分だけ少ない）
export const ROWS_REST = 19   // 2ページ目以降

export const COL_LABELS = ['月日', '発地名', '着地名', '車番', '品名', '数量', '単位', 'ｔ数', '単価', '基本運賃', '割増', '備考']

// ---- 数値 ----
const num = (n) => { const v = Number(n); return Number.isFinite(v) ? v : 0 }
// 常に half-up（0.5 は切り上げ）。Math.round は負値で挙動が変わるが請求額は非負。
export function roundYen(n) { return Math.floor(num(n) + 0.5) }
export const yen = (n) => Math.round(num(n))

/**
 * 通行料内税の税計算。実物15件で完全一致を確認済み。
 * @param {number} fareTotal 基本運賃の合計（外税対象金）
 * @param {number} tollGross 通行料の合計（税込）
 */
export function invoiceTax(fareTotal, tollGross) {
  const fare = yen(fareTotal)
  const gross = yen(tollGross)
  // /1.1 ではなく ×10/11。1.1 は二進で表せず、境界値でぶれる余地を残さないため
  const tollNet = gross > 0 ? roundYen(gross * 10 / 11) : 0
  const tollTax = gross - tollNet
  const taxable = fare + tollNet
  const tax = roundYen(taxable / 10)
  return { fareTotal: fare, tollGross: gross, tollNet, tollTax, taxable, tax, total: taxable + tax }
}

// ---- 表記（実物の字形をそのまま再現する） ----
export const fullWidthDigits = (s) => String(s == null ? '' : s).replace(/[0-9]/g, (c) => String.fromCharCode(c.charCodeAt(0) + 0xFEE0))
export const money = (n) => yen(n).toLocaleString('ja-JP')

const ymOf = (m) => { const x = /^(\d{4})-(\d{2})$/.exec(String(m || '')); return x ? [Number(x[1]), Number(x[2])] : null }

// 請求日は常に月末（実物: ファイル名が 20260815 の1件も紙の日付は「８月３１日」だった）
export function monthEndISO(month) {
  const ym = ymOf(month); if (!ym) return ''
  const d = new Date(Date.UTC(ym[0], ym[1], 0))
  return `${ym[0]}-${String(ym[1]).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
}
// 「２０２６年　７月３１日」＝全角数字・年と月の間に全角スペース1（実物 bbox 幅 176.1px＝全角11文字）
export function invoiceDateJa(month) {
  const ym = ymOf(month); if (!ym) return ''
  const day = new Date(Date.UTC(ym[0], ym[1], 0)).getUTCDate()
  return `${fullWidthDigits(ym[0])}年　${fullWidthDigits(ym[1])}月${fullWidthDigits(day)}日`
}
// 月日セルは「 7/ 1」。月・日とも幅2で右詰め＝実物の等幅の見え方と一致する
export function dayCell(iso) {
  const x = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || '')); if (!x) return ''
  return `${String(Number(x[2])).padStart(2, ' ')}/${String(Number(x[3])).padStart(2, ' ')}`
}

// 得意先マスタ（customers テーブル）の docId。masters/seedMasters.js の codeStr と同じ規則
export function customerDocId(code, branchNo) {
  if (code == null || code === '') return ''
  const base = String(code).padStart(6, '0')
  return (branchNo == null || branchNo === '') ? base : `${base}-${branchNo}`
}

// ---- 行の組み立てとページ分割 ----
/**
 * 明細 → 空行1 → 【10％】対象金額 → (通行料>0 なら内訳3行) → 【10％】消費税額。
 * 「まとめて次ページへ送る」処理は無い（実物でも内訳ブロックがページ境界で分断されている）。
 */
export function buildInvoiceRows(runs, t) {
  const rows = (runs || []).map((r) => ({
    type: 'detail',
    day: dayCell(r.unko), a: r.a || '', b: r.b || '', sha: r.sha || '', hin: r.hin || '',
    qty: r.qty || '', unit: r.unit || '', ton: r.ton || '', price: r.price || '',
    amt: yen(r.amt), wari: yen(r.wari), toll: yen(r.toll), biko: r.biko || '',
  }))
  rows.push({ type: 'blank' })
  rows.push({ type: 'total', label: '【10％】対象金額', amount: t.taxable, bold: true })
  // 通行料 0 の得意先は内訳3行を丸ごと省く（実物10件がこの形）
  if (t.tollGross > 0) {
    rows.push({ type: 'total', label: '内訳:外税対象金', amount: t.fareTotal })
    rows.push({ type: 'total', label: '通行料', amount: t.tollGross })          // ここは**税込**
    rows.push({ type: 'total', label: '（内税額)', amount: t.tollTax, paren: true })
  }
  rows.push({ type: 'total', label: '【10％】消費税額', amount: t.tax, bold: true })
  return rows
}

export function paginateRows(rows, first = ROWS_FIRST, rest = ROWS_REST) {
  const all = rows || []
  const pages = []
  let i = 0
  do {
    const cap = pages.length === 0 ? first : rest
    pages.push(all.slice(i, i + cap))
    i += cap
  } while (i < all.length)
  return pages
}

// ---- 集計1グループ → 紙1枚分のモデル ----
/**
 * @param {object} g buildSheets の得意先グループ（name/code/branchNo/sub/toll/runs）
 * @param {string} month 'YYYY-MM'
 * @param {object} company resolveCompany() 済みの自社情報
 * @param {object} custInfo resolveCustomerInfo() 済み or 生の customers ドキュメント
 */
export function invoiceModel(g, month, company, custInfo) {
  const t = invoiceTax(g && g.sub, g && g.toll)
  const info = (custInfo && typeof custInfo === 'object' && 'addr1' in custInfo) ? custInfo : _resolveCust(custInfo)
  return {
    month,
    dateJa: invoiceDateJa(month),
    dateISO: monthEndISO(month),
    company,
    customer: { name: (g && g.name) || '', docId: customerDocId(g && g.code, g && g.branchNo), ...info },
    tax: t,
    // サマリ帯5枠。「通行料」は**税抜**（末尾内訳の税込と別物）
    summary: { fare: t.fareTotal, toll: t.tollNet, taxable: t.taxable, tax: t.tax, total: t.total },
    pages: paginateRows(buildInvoiceRows((g && g.runs) || [], t)),
    missing: _missing(info),
  }
}

// companyInfo.core.js と同じ規則。循環importを避けるためここに最小版を置く（正本はあちら）
const _S = (v) => (typeof v === 'string' ? v.trim() : '')
const _resolveCust = (d) => ({ zip: _S(d && d.zip), addr1: _S(d && d.addr1), addr2: _S(d && d.addr2), tel: _S(d && d.tel), fax: _S(d && d.fax) })
const _missing = (c) => [['zip', '〒'], ['addr1', '住所'], ['tel', 'TEL'], ['fax', 'FAX']].filter(([k]) => !c[k]).map(([, l]) => l)
